import React, { useState, useEffect } from "react";
import styled from "styled-components";
import API from "../../../util/api";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";
import { showErrorModal } from "../../../util/general";


const Container = styled.div`
  background: #f8f8f8;
  border-radius: 5px;
  box-shadow: 2px 5px 5px rgba(0, 0, 0, 0.05);
  padding: 1.5em;
  max-width: 800px;
  margin: auto;
`;

const Title = styled.h1`
  font-size: 1.5em;
  margin-bottom: 1em;
  color: ${({ colors }) => colors.labelText};
`;

const Table = styled.table`
  ${adminTable};
  font-size: 0.95em;
  margin-bottom: 1.5em;
  background: #fafafa;

  th,
  td {
    padding: 0.75em;
    border-bottom: 1px solid #ccc;
  }

  th {
    text-align: left;
    font-weight: 600;
    color: ${({ colors }) => colors.labelText};
    border-bottom: 1px solid #ccc;
  }

  tr:nth-child(even) {
    background: #f5f5f5;
  }

  .reg-code {
    font-family: monospace;
  }
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  input {
    margin-right: 0.5em;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 1em;
`;

const Button = styled.button`
  padding: 0.6em 1.2em;
  font-size: 1em;
  border-radius: 4px;
  border: ${({ primary, colors }) =>
    primary ? "none" : `1px solid ${colors.border}`};
  background: ${({ primary, colors }) =>
    primary ? colors.buttonBackground : colors.cardBackground};
  color: ${({ primary }) => (primary ? "white" : "inherit")};
  cursor: pointer;
  &:hover {
    background: ${({ primary, colors }) =>
      primary ? colors.buttonHoverBackground : colors.buttonBackground};
    color: white;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default function TeacherSettingsContent({ colors}) {
  // Default fallback values if API returns nothing
  const subscription = useSelector(s => s.auth.selected_subscriptions) || {};
  const invoice_id    = subscription.invoice_id;
  const invoice_number = subscription.invoice_number;
  const location = useLocation();
  const locationMode = location.state?.mode;
  const [mode, setMode] = useState("view");
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setMode(locationMode || "view");
  }, [location.key, locationMode]);

  const toggle = (key) =>
    setSettings((s) => ({ ...s, [key]: !s[key] }));

  // 1) Fetch settings from server whenever invoiceId changes
  useEffect(() => {
    if (!invoice_id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    API.getSettings({ invoice_id})
      .then((resp) => {
        if (
          resp.status === "Success" &&
          Array.isArray(resp.settings) &&
          resp.settings.length > 0
        ) {
          const s = resp.settings[0];
          setSettings({
            onlineRegistrationEnabled:
              s.online_registration === 1 || s.online_registration === "1",
            registrationCode: s.invoice_number || "",
            mustCertify:
              s.certify_implementation === "1" ||
              s.certify_implementation === 1,
            autoReimport:
              s.auto_reimport === "1" || s.auto_reimport === 1,
          });
        } else {
          // Fallback if no valid settings returned
          setSettings(null);
        }
      })
      .catch((err) => {
        console.error("Failed to load settings:", err);
        setSettings(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [invoice_id]);

  // 2) When the user clicks “Save Changes”, call API.saveSettings
  const handleSave = async () => {
    // Build the payload exactly like your Postman example
    const payload = {
      invoice_id: invoice_id,
      invoice_number,
      online_registration: settings.onlineRegistrationEnabled ? 1 : 0,
      certify_implementation: settings.mustCertify ? 1 : 0,
      auto_reimport: settings.autoReimport ? 1 : 0,
      // Note: The API does not allow editing invoice_number here— 
      // that value remains read-only in this UI.
    };

    try {
      const resp = await API.updateSettings(payload);
      if (resp.status === "Success") {
        alert("Settings saved successfully!");
        setMode("view");
      } else {
        // The API responded with an error message
        showErrorModal("Save failed: " + (resp.message || "Unknown error"));
      }
    } catch (err) {
      showErrorModal("Save failed: " + err.message);
      console.error(err);
    }
  };

  const handleCancel = () => {
    // Simply exit “edit” mode without saving. We keep settings in local state as-is.
    setMode("view");
  };

  if (loading) {
    return (
      <Container>
        <p>Loading settings…</p>
      </Container>
    );
  }

  return (
    <Container>
      {mode === "view" ? (
        <>
          <Title colors={colors}>Settings</Title>
          <Table colors={colors}>
            <tbody>
              <tr>
                <th>Online Registration Enabled:</th>
                <td>
                  {settings.onlineRegistrationEnabled ? "Yes" : "No"}
                </td>
              </tr>
              <tr>
                <th>Online Registration Code:</th>
                <td className="reg-code">
                  {settings.registrationCode}
                </td>
              </tr>
              <tr>
                <th>Teachers Must Certify Implementations:</th>
                <td>{settings.mustCertify ? "Yes" : "No"}</td>
              </tr>
              <tr>
                <th>Auto Re-import Implementations:</th>
                <td>{settings.autoReimport ? "Yes" : "No"}</td>
              </tr>
            </tbody>
          </Table>
          <ButtonRow>
            <Button colors={colors} primary onClick={() => setMode("edit")}>
              Edit
            </Button>
          </ButtonRow>
        </>
      ) : (
        <>
          <Title colors={colors}>Edit Settings</Title>
          <Table colors={colors}>
            <tbody>
              <tr>
                <th>Online Registration Enabled:</th>
                <td>
                  <CheckboxRow>
                    <input
                      type="checkbox"
                      checked={settings.onlineRegistrationEnabled}
                      onChange={() =>
                        toggle("onlineRegistrationEnabled")
                      }
                    />
                    Enable
                  </CheckboxRow>
                </td>
              </tr>
              <tr>
                <th>Online Registration Code:</th>
                <td className="reg-code">
                  {settings.registrationCode}
                </td>
              </tr>
              <tr>
                <th>Teachers Must Certify Implementations:</th>
                <td>
                  <CheckboxRow>
                    <input
                      type="checkbox"
                      checked={settings.mustCertify}
                      onChange={() => toggle("mustCertify")}
                    />
                    Require Certification
                  </CheckboxRow>
                </td>
              </tr>
              <tr>
                <th>Auto Re-import Implementations:</th>
                <td>
                  <CheckboxRow>
                    <input
                      type="checkbox"
                      checked={settings.autoReimport}
                      onChange={() => toggle("autoReimport")}
                    />
                    Enable Auto Re-import
                  </CheckboxRow>
                </td>
              </tr>
            </tbody>
          </Table>
          <ButtonRow>
            <Button colors={colors} onClick={handleCancel}>
              Cancel
            </Button>
            <Button colors={colors} primary onClick={handleSave}>
              Save Changes
            </Button>
          </ButtonRow>
        </>
      )}
    </Container>
  );
}
